import React, { memo } from 'react';
import { SwipeableDrawer, Theme, Toolbar, IconButton, Typography, List, ListItemButton, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { QueryKeys } from 'src/core/declarations/enum';
import { IProduct, IQRCodeData } from 'src/core/declarations/app';
import { getCompareProducts } from 'src/crud/crud';
import { LoadingBox, CompareProductContent } from 'src/components';
import { useAppContext } from 'src/core/store';
import { sortFunction } from 'src/core/helpers';

interface ICompareDrawerProps {
  drawerOpen: boolean;
  onDrawerToggle?: Function;
  onCompareDetails?: (compareProductId: string) => void;
}

const ThemedSwipeableDrawer = styled(SwipeableDrawer)(({ theme }: { theme: Theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.black.main,
    width: '212px',
    overflow: 'hidden',
    paddingBottom: '20px'
  }
}))

const CompareDrawer = memo(({ drawerOpen, onDrawerToggle, onCompareDetails }: ICompareDrawerProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const { productClaimToggleEvent } = useAppContext();
  const qrCodeData = queryClient.getQueryData<IQRCodeData>(QueryKeys.qrCode);
  const product = queryClient.getQueryData(QueryKeys.product) as IProduct;
  const { id, name, categoryId } = product;

  const handleCompareProduct = (compareProductId: string) => {
    if (onCompareDetails) onCompareDetails(compareProductId);
  }

  const { isLoading, data } = useQuery(
    QueryKeys.compareProducts,
    () => getCompareProducts(id, qrCodeData?.id as string, categoryId, i18n.language),
    {
      enabled: drawerOpen
    });

  const handleDrawerToggle = (shouldDrawerOpen: boolean) => {
    if (onDrawerToggle) onDrawerToggle(shouldDrawerOpen);
    if (productClaimToggleEvent) productClaimToggleEvent.next(!shouldDrawerOpen);
  }

  return (
    <ThemedSwipeableDrawer
      variant="persistent"
      anchor="right"
      open={drawerOpen}
      onClose={() => handleDrawerToggle(false)}
      onOpen={() => handleDrawerToggle(true)}
    >
      <Toolbar />
      <Grid
        sx={{
          px: 2,
          boxShadow: '0px 2px 4px -1px rgb(0 0 0 / 30%), 0px 4px 5px 0px rgb(0 0 0 / 20%), 0px 1px 10px 0px rgb(0 0 0 / 15%)',
          zIndex: 1
        }}
      >
        <IconButton sx={{
          pl: 0,
          pr: 2,
          transform: 'translateX(-10px)',
          mr: 'auto'
        }} onClick={() => handleDrawerToggle(false)}>
          <KeyboardArrowRightIcon sx={{ color: theme => theme.palette.white.main }} />
        </IconButton>

        <Typography
          variant="h6"
          sx={{ whiteSpace: 'pre-wrap', fontWeight: 'bold', mb: 2 }}
        >{t('ArPageCompareTitle', { productName: name })}</Typography>

      </Grid>

      {isLoading
        ? (<LoadingBox />)
        : (<List sx={{
          overflow: 'auto',
          pt: 0,
          px: 2
        }}>
          {data && data.sort(sortFunction).map((p) => (<ListItemButton key={`compare-item-${p.id}`}
            sx={{
              width: '180px',
              height: '180px',
              position: 'relative',
              p: 0,
              mb: 1
            }}
            onClick={() => handleCompareProduct(p.id)}
          >
            <CompareProductContent product={p} />
          </ListItemButton >))}
        </List>)
      }

    </ThemedSwipeableDrawer >
  )
});

export default CompareDrawer;
